// Authentication service
import axios from "axios";
import {
  getApiBaseUrl,
  getHeaders,
  handleApiError,
  ApiResponse,
} from "./apiClient";
import type { SignupContext } from "@shared/onboarding";

export type User = {
  _id: string;
  name?: string;
  email?: string;
  phoneNumber?: string;
  full_name?: string;
  role?: "user" | "admin" | "store_owner";
  provider?: string;
  phoneVerified?: boolean;
  points?: number;
  isAdmin: boolean;
  // Derived server-side via getEffectiveRole (quick 260929-j7k): true for moderators and admins.
  isModerator?: boolean;
  createdAt: string;
  updatedAt: string;
};

export type AuthResponse = {
  user: User;
  token: string;
  isNewUser?: boolean;
};

export type LoginCredentials = {
  email: string;
  password: string;
};

export type SocialLoginData = {
  credential?: string; // Google ID token (GSI button / One Tap flow)
  accessToken?: string; // Google OAuth access token (custom-button useGoogleLogin flow)
  // Phase 49 (D-04): how the signup happened. Stored only when the account is new; the backend
  // allowlists it.
  signupContext?: SignupContext;
};

export type RegisterData = {
  name: string;
  email: string;
  password: string;
};

// Phone authentication types
export type PhoneAuthInitiateData = {
  phoneNumber: string;
  email?: string;
};

export type PhoneAuthSendOtpData = {
  transactionId: string;
};

export type PhoneAuthVerifyData = {
  transactionId: string;
  otp: string;
  // D-16: explicit WhatsApp consent from the OTP-step checkbox. Sent only when true.
  whatsappOptIn?: boolean;
  // Phase 49 (D-04): signup context, stored only when verify creates the account.
  signupContext?: SignupContext;
};

export type PhoneAuthStatusResponse = {
  status: "pending" | "sent" | "verified" | "expired" | "failed";
  attempts: number;
  maxAttempts: number;
};

const AUTH_STORAGE_KEY =
  import.meta.env.VITE_AUTH_STORAGE_KEY || "yetfarrag_auth";

/**
 * Login user with email and password
 */
export const login = async ({
  email,
  password,
}: LoginCredentials): Promise<ApiResponse<AuthResponse>> => {
  try {
    const response = await fetch(`${getApiBaseUrl()}/auth/login`, {
      method: "POST",
      headers: getHeaders(false),
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: {
          message: data.message || "Login failed",
          status: response.status,
          details: data,
        },
      };
    }

    // Store authentication data
    localStorage.setItem(
      AUTH_STORAGE_KEY,
      JSON.stringify({
        token: data.token,
        user: data.user,
      })
    );

    return {
      success: true,
      data: data as AuthResponse,
    };
  } catch (error) {
    return {
      success: false,
      error: handleApiError(error),
    };
  }
};

/**
 * Register a new user
 */
/**
 * Login with Google
 */
export const loginWithGoogle = async (
  socialData: SocialLoginData
): Promise<ApiResponse<AuthResponse>> => {
  try {
    const response = await fetch(`${getApiBaseUrl()}/auth/google`, {
      method: "POST",
      headers: getHeaders(false),
      body: JSON.stringify(socialData),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: {
          message: data.error || "Google login failed",
          status: response.status,
          details: data,
        },
      };
    }

    // Store authentication data
    localStorage.setItem(
      AUTH_STORAGE_KEY,
      JSON.stringify({
        token: data.data.token,
        user: data.data.user,
      })
    );

    return {
      success: true,
      data: data.data as AuthResponse,
    };
  } catch (error) {
    return {
      success: false,
      error: handleApiError(error),
    };
  }
};

export const register = async (
  userData: RegisterData
): Promise<ApiResponse<AuthResponse>> => {
  try {
    const response = await fetch(`${getApiBaseUrl()}/auth/register`, {
      method: "POST",
      headers: getHeaders(false),
      body: JSON.stringify(userData),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: {
          message: data.message || "Registration failed",
          status: response.status,
          details: data,
        },
      };
    }

    // Store authentication data
    localStorage.setItem(
      AUTH_STORAGE_KEY,
      JSON.stringify({
        token: data.token,
        user: data.user,
      })
    );

    return {
      success: true,
      data: data as AuthResponse,
    };
  } catch (error) {
    return {
      success: false,
      error: handleApiError(error),
    };
  }
};

/**
 * Logout user
 */
export const logout = (): void => {
  localStorage.removeItem(AUTH_STORAGE_KEY);
};

/**
 * Get current authenticated user
 */
export const getCurrentUser = (): User | null => {
  try {
    const authData = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!authData) return null;

    const { user } = JSON.parse(authData);
    return user as User;
  } catch (error) {
    console.error("Error getting current user:", error);
    return null;
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return !!getCurrentUser();
};

/**
 * Get current auth token
 */
export const getAuthToken = (): string | null => {
  try {
    // First try the new format used by AuthContext
    const token = localStorage.getItem("auth_token");
    if (token) return token;

    // Fallback to old format
    const authData = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!authData) return null;

    const parsed = JSON.parse(authData);
    return parsed.token;
  } catch {
    return null;
  }
};

export const syncFacebookSenderId = async ({
  facebookSenderId,
  userId,
}: {
  facebookSenderId: string;
  userId: string;
}): Promise<ApiResponse<null>> => {
  return axios({
    url: `${getApiBaseUrl()}/users/facebook/sync`,
    method: "POST",
    headers: getHeaders(),
    data: { facebookSenderId, userId },
  });
};

// Phone Authentication Functions

/**
 * Initiate phone authentication process
 */
export const initiatePhoneAuth = async ({
  phoneNumber,
  email,
}: PhoneAuthInitiateData): Promise<ApiResponse<{ transactionId: string }>> => {
  try {
    const response = await fetch(`${getApiBaseUrl()}/auth/phone/initiate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ phoneNumber, email }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: {
          message: data.error || "حدث خطأ في إنشاء طلب المصادقة",
          status: response.status,
        },
      };
    }

    return {
      success: true,
      data: data.data,
    };
  } catch (error) {
    console.error("Error initiating phone auth:", error);
    return {
      success: false,
      error: {
        message: "حدث خطأ في الاتصال", // Connection error occurred
      },
    };
  }
};

/**
 * Send OTP to phone number
 */
export const sendPhoneOtp = async ({
  transactionId,
}: PhoneAuthSendOtpData): Promise<ApiResponse<{ verificationId: string }>> => {
  try {
    const response = await fetch(`${getApiBaseUrl()}/auth/phone/send-otp`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ transactionId }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: {
          message: data.error || "حدث خطأ في إرسال رمز التحقق",
          status: response.status,
        },
      };
    }

    return {
      success: true,
      data: data.data,
    };
  } catch (error) {
    console.error("Error sending phone OTP:", error);
    return {
      success: false,
      error: {
        message: "حدث خطأ في الاتصال", // Connection error occurred
      },
    };
  }
};

/**
 * Verify phone authentication with OTP
 */
export const verifyPhoneAuth = async ({
  transactionId,
  otp,
  whatsappOptIn,
  signupContext,
}: PhoneAuthVerifyData): Promise<ApiResponse<AuthResponse>> => {
  try {
    const response = await fetch(`${getApiBaseUrl()}/auth/phone/verify`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        transactionId,
        otp,
        // A real JSON boolean; the backend ignores anything but `true` (46-09).
        ...(whatsappOptIn === true ? { whatsappOptIn: true } : {}),
        ...(signupContext ? { signupContext } : {}),
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: {
          message: data.error || "حدث خطأ في التحقق من الرمز",
          status: response.status,
        },
      };
    }

    // Don't store auth data here - let the AuthContext handle it
    const authData = {
      user: data.data.user,
      token: data.data.token,
      isNewUser: data.data.isNewUser,
    };

    return {
      success: true,
      data: authData,
    };
  } catch (error) {
    console.error("Error verifying phone auth:", error);
    return {
      success: false,
      error: {
        message: "حدث خطأ في الاتصال", // Connection error occurred
      },
    };
  }
};

/**
 * Get phone authentication status
 */
export const getPhoneAuthStatus = async (
  transactionId: string
): Promise<ApiResponse<PhoneAuthStatusResponse>> => {
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/auth/phone/status/${transactionId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: {
          message: data.error || "حدث خطأ في الحصول على حالة المصادقة",
          status: response.status,
        },
      };
    }

    return {
      success: true,
      data: data.data,
    };
  } catch (error) {
    console.error("Error getting phone auth status:", error);
    return {
      success: false,
      error: {
        message: "حدث خطأ في الاتصال", // Connection error occurred
      },
    };
  }
};

/**
 * Update user profile information
 */
export const updateUserProfile = async (profileData: {
  fullName?: string;
  location?: string;
  preferences?: {
    emailNotifications?: boolean;
    pushNotifications?: boolean;
    marketingEmails?: boolean;
    language?: string;
    theme?: string;
  };
  privacy?: {
    showEmail?: boolean;
    profileVisibility?: 'public' | 'private';
  };
}): Promise<ApiResponse<User>> => {
  try {
    const token = getAuthToken();
    if (!token) {
      return {
        success: false,
        error: {
          message: "يجب تسجيل الدخول أولاً",
        },
      };
    }

    const response = await fetch(`${getApiBaseUrl()}/users/profile`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        full_name: profileData.fullName,
        location: profileData.location,
        preferences: profileData.preferences,
        privacy: profileData.privacy,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: {
          message: data.error || "حدث خطأ في تحديث الملف الشخصي",
          status: response.status,
        },
      };
    }

    // Update stored auth data with new user info
    const authData = localStorage.getItem(AUTH_STORAGE_KEY);
    if (authData) {
      const parsedAuthData = JSON.parse(authData);
      parsedAuthData.user = { ...parsedAuthData.user, ...data.data.user };
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(parsedAuthData));
    }

    return {
      success: true,
      data: data.data.user,
    };
  } catch (error) {
    console.error("Error updating user profile:", error);
    return {
      success: false,
      error: {
        message: "حدث خطأ في الاتصال",
      },
    };
  }
};

/**
 * Upload user avatar
 */
export const uploadAvatar = async (file: File): Promise<ApiResponse<{ avatarUrl: string }>> => {
  try {
    const token = getAuthToken();
    if (!token) {
      return {
        success: false,
        error: {
          message: "يجب تسجيل الدخول أولاً",
        },
      };
    }

    // Use FormData for efficient file upload
    const formData = new FormData();
    formData.append('avatar', file);

    const response = await fetch(`${getApiBaseUrl()}/users/avatar`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        // Don't set Content-Type header - let the browser set it with boundary for FormData
      },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: {
          message: data.error || "حدث خطأ في تحديث الصورة الشخصية",
          status: response.status,
        },
      };
    }

    // Update stored auth data with new avatar URL
    const authData = localStorage.getItem(AUTH_STORAGE_KEY);
    if (authData) {
      const parsedAuthData = JSON.parse(authData);
      parsedAuthData.user = { ...parsedAuthData.user, avatar_url: data.data.avatarUrl };
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(parsedAuthData));
    }

    return {
      success: true,
      data: { avatarUrl: data.data.avatarUrl },
    };
  } catch (error) {
    console.error("Error uploading avatar:", error);
    return {
      success: false,
      error: {
        message: "حدث خطأ في الاتصال",
      },
    };
  }
};
